/**
 * Thin fetch wrapper for the dykkerbutikken API.
 *
 * Every call is sent with `credentials: 'include'`, because auth is an HttpOnly session cookie
 * rather than a bearer token in localStorage (nothing for XSS to steal).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail: string | null,
    readonly errors: Record<string, string[]> = {},
  ) {
    super(detail || title)
    this.name = 'ApiError'
  }

  /** Flattened field errors, ready for a form summary. */
  get fieldMessages(): string[] {
    return Object.values(this.errors).flat()
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

type QueryValue = string | number | boolean | null | undefined

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options

  const url = new URL(path, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (response.status === 204) return undefined as T

  const payload = await readJson(response)

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.title ?? response.statusText ?? 'Forespørgslen mislykkedes',
      payload?.detail ?? null,
      payload?.errors ?? {},
    )
  }

  return payload as T
}

async function readJson(response: Response): Promise<any> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return { title: 'Uventet svar fra serveren', detail: text.slice(0, 500) }
  }
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
}
